import { ref, watch, nextTick, onScopeDispose, type Ref, type WatchSource } from 'vue'

/**
 * Registry of every mounted field's `flushNow`. Lets callers commit all
 * in-flight buffered keystrokes BEFORE they replace or evaluate reactive config
 * state, so pending local input cannot later overwrite a newer server draft.
 * Used by configStore's foreign-change/stale-save handling. (ENG-1024/ENG-1066)
 */
const pendingFieldFlushers = new Set<() => void>()
let pendingDebouncedFieldCount = 0

export function hasPendingDebouncedFields(): boolean {
  return pendingDebouncedFieldCount > 0
}


export function flushPendingDebouncedFields(): boolean {
  const hadPendingFields = hasPendingDebouncedFields()
  pendingFieldFlushers.forEach((flush) => flush())
  return hadPendingFields
}

/**
 * Creates a local buffered ref that debounces writes back to a reactive source.
 *
 * Solves the "every keystroke triggers a config mutation" problem by keeping a
 * local copy that the input binds to, flushing changes to the source only after
 * a debounce delay. External changes (undo/redo, AI generation) sync back
 * immediately and cancel any pending flush.
 *
 * @param getter  - Reactive getter for the source value (e.g. `() => props.node.name`)
 * @param setter  - Function to write back to the source (e.g. `(v) => props.node.name = v`)
 * @param options - `delay` in ms (default 500)
 * @returns A writable ref to bind with v-model
 *
 * @example
 * const localName = useDebouncedField(
 *   () => props.node.name,
 *   (v) => { props.node.name = v },
 * )
 * // <InputText v-model="localName" />
 */
export function useDebouncedField<T>(
  getter: () => T,
  setter: (val: T) => void,
  options?: { delay?: number },
): Ref<T> & { flushNow: () => void } {
  const delay = options?.delay ?? 500

  const local = ref(getter()) as Ref<T>
  let pendingFlush = false
  let externallyUpdating = false
  let flushTimer: ReturnType<typeof setTimeout> | null = null

  function setPendingFlush(value: boolean) {
    if (pendingFlush === value) return

    pendingFlush = value
    pendingDebouncedFieldCount += value ? 1 : -1
  }


  function cancelPendingFlush() {
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
  }

  function scheduleFlush(value: T) {
    cancelPendingFlush()
    flushTimer = setTimeout(() => {
      flushTimer = null
      setPendingFlush(false)
      setter(value)
    }, delay)
  }

  function flush(value: T) {
    cancelPendingFlush()
    setPendingFlush(false)
    setter(value)
  }

  // Local changes → debounced write to source
  watch(local, (newValue) => {
    if (externallyUpdating) return
    setPendingFlush(true)
    scheduleFlush(newValue)
  })

  // External changes → immediate sync to local unless the user has a pending
  // local edit. Pending local input wins because configStore flushes these
  // buffers before applying foreign refetches or resolving stale-save rejections.
  watch(getter as WatchSource<T>, (newValue) => {
    if (newValue !== local.value) {
      if (pendingFlush) {
        return
      }
      externallyUpdating = true
      local.value = newValue
      nextTick(() => { externallyUpdating = false })
    }
  })

  function flushNow() {
    if (pendingFlush) {
      flush(local.value)
    }
  }

  pendingFieldFlushers.add(flushNow)
  onScopeDispose(() => {
    pendingFieldFlushers.delete(flushNow)
    // Cancel (do NOT flush) on unmount: the target may be detached/replaced,
    // so a trailing setter write would be lost or land on a stale object.
    cancelPendingFlush()
    setPendingFlush(false)
  })

  const result = local as Ref<T> & { flushNow: () => void }
  result.flushNow = flushNow
  return result
}
